/**
 * @fileoverview Aba "CRM" do editor de triagem: o card da triagem num quadro do
 * CRM. O card nasce na entrada quando a triagem começa, vai para "em triagem"
 * enquanto a IA conduz (ou para a coluna de uma regra de item), para a de
 * atenção quando a triagem chama uma pessoa e para a de concluídas no fim. Card
 * que alguém move para outra coluna fica onde a pessoa deixou.
 */

import React from "react";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { Plus, Trash2 } from "lucide-react";

import { i18n } from "../../../../translate/i18n";
import ModernSelect from "../../../../components/ModernSelect";
import { newCrmRule } from "./formState";
import useStyles from "./styles";

const c = (key, params) => i18n.t(`triage.manager.editor.crm.${key}`, params);

const WHEN = ["awaiting", "only_pending"];

const CrmEditor = ({ value, onChange, boards, items }) => {
  const classes = useStyles();
  const quadro = boards.find((b) => String(b.id) === String(value.boardId)) || null;
  const colunas = quadro?.columns || [];
  const set = (patch) => onChange({ ...value, ...patch });
  const itensComChave = items.filter((it) => it.key);

  // Quadro novo: a entrada (a coluna de entrada dele) e a primeira de concluídas já vêm escolhidas.
  const escolherQuadro = (id) => {
    const novo = boards.find((b) => String(b.id) === String(id));
    const cols = novo?.columns || [];
    const entrada = cols.find((col) => col.isDefault) || cols[0];
    const concluida = cols.find((col) => col.isCompleted && col.id !== entrada?.id);
    onChange({
      ...value,
      boardId: id,
      entry: entrada?.id ?? "",
      inProgress: "",
      attention: "",
      completed: concluida?.id ?? "",
      rules: [],
    });
  };

  const setRegra = (uid, patch) => set({ rules: value.rules.map((r) => (r.uid === uid ? { ...r, ...patch } : r)) });

  // A coluna de concluídas é só das triagens concluídas: não entra nos outros estados nem nas regras.
  const semConcluidas = colunas.filter((col) => String(col.id) !== String(value.completed));

  const coluna = (key, { optional = false, lista = colunas } = {}) => (
    <ModernSelect
      className={classes.field}
      size="small"
      fullWidth
      label={c(key)}
      helperText={c(`${key}Hint`)}
      InputLabelProps={{ shrink: true }}
      displayEmpty
      value={value[key]}
      onChange={(ev) => set({ [key]: ev.target.value })}
    >
      <MenuItem value="">
        <em>{optional ? c("columnNone") : c("columnChoose")}</em>
      </MenuItem>
      {lista.map((col) => (
        <MenuItem key={col.id} value={col.id}>
          {col.name}
        </MenuItem>
      ))}
    </ModernSelect>
  );

  return (
    <div className={classes.form}>
      <div className={classes.hint}>{c("intro")}</div>

      <FormControlLabel
        className={classes.check}
        control={<Switch color="primary" checked={value.enabled} onChange={(ev) => set({ enabled: ev.target.checked })} />}
        label={c("enabled")}
      />

      {value.enabled && boards.length === 0 && <div className={classes.warnText}>{c("noBoards")}</div>}

      {value.enabled && boards.length > 0 && (
        <>
          <ModernSelect
            className={classes.field}
            size="small"
            fullWidth
            label={c("board")}
            InputLabelProps={{ shrink: true }}
            displayEmpty
            value={value.boardId}
            onChange={(ev) => escolherQuadro(ev.target.value)}
          >
            <MenuItem value="">
              <em>{c("boardNone")}</em>
            </MenuItem>
            {boards.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </ModernSelect>

          {quadro && (
            <>
              <div className={classes.grid2}>
                {coluna("entry")}
                {coluna("inProgress", { optional: true, lista: semConcluidas })}
                {coluna("attention", { optional: true, lista: semConcluidas })}
                {coluna("completed")}
              </div>

              <div>
                <div className={classes.label}>{c("rules")}</div>
                <div className={classes.hint}>{c("rulesHint")}</div>
              </div>
              {value.rules.length > 0 && (
                <div className={classes.fieldsBox}>
                  {value.rules.map((r) => {
                    // "Único obrigatório que falta" só vale para item obrigatório.
                    const itens = r.when === "only_pending" ? itensComChave.filter((it) => it.required) : itensComChave;
                    return (
                      <div key={r.uid} className={classes.fieldRow}>
                        <ModernSelect
                          className={classes.field}
                          size="small"
                          label={c("ruleItem")}
                          InputLabelProps={{ shrink: true }}
                          displayEmpty
                          value={itens.some((it) => it.key === r.requirementKey) ? r.requirementKey : ""}
                          onChange={(ev) => setRegra(r.uid, { requirementKey: ev.target.value })}
                        >
                          <MenuItem value="">
                            <em>{c("ruleItemChoose")}</em>
                          </MenuItem>
                          {itens.map((it) => (
                            <MenuItem key={it.uid} value={it.key}>
                              {it.name.trim() || it.key}
                            </MenuItem>
                          ))}
                        </ModernSelect>
                        <ModernSelect
                          className={classes.field}
                          size="small"
                          label={c("ruleWhen")}
                          value={r.when}
                          onChange={(ev) => setRegra(r.uid, { when: ev.target.value })}
                        >
                          {WHEN.map((wh) => (
                            <MenuItem key={wh} value={wh}>
                              {c(`whenOptions.${wh}`)}
                            </MenuItem>
                          ))}
                        </ModernSelect>
                        <ModernSelect
                          className={classes.field}
                          size="small"
                          label={c("ruleColumn")}
                          InputLabelProps={{ shrink: true }}
                          displayEmpty
                          value={semConcluidas.some((col) => String(col.id) === String(r.columnId)) ? r.columnId : ""}
                          onChange={(ev) => setRegra(r.uid, { columnId: ev.target.value })}
                        >
                          <MenuItem value="">
                            <em>{c("columnChoose")}</em>
                          </MenuItem>
                          {semConcluidas.map((col) => (
                            <MenuItem key={col.id} value={col.id}>
                              {col.name}
                            </MenuItem>
                          ))}
                        </ModernSelect>
                        <Tooltip title={c("ruleRemove")}>
                          <button
                            type="button"
                            className={classes.iconBtn}
                            onClick={() => set({ rules: value.rules.filter((o) => o.uid !== r.uid) })}
                            aria-label={c("ruleRemove")}
                          >
                            <Trash2 />
                          </button>
                        </Tooltip>
                      </div>
                    );
                  })}
                </div>
              )}
              <div>
                <button type="button" className={classes.btnGhost} onClick={() => set({ rules: [...value.rules, newCrmRule()] })}>
                  <Plus /> {c("addRule")}
                </button>
              </div>

              <div className={classes.noteBox}>
                <div>{c("manualNote")}</div>
                <div>{c("cancelNote")}</div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default CrmEditor;
